import { Button, SegmentedControl, Select, TextInput } from '@mantine/core';
import { IconDownload, IconReportAnalytics, IconSearch } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../EmptyState.js';
import { formatDateTime, formatDay } from '../../../format.js';
import { offsetLabel } from '../../../inputs/lt-utc.js';
import { PageHeader } from '../../../PageHeader.js';
import { Panel } from '../../../Panel.js';
import { StatusBadge } from '../../../StatusBadge.js';
import { useShell } from '../../../shell-context.js';
import table from '../../materials/Materials.module.css';
import { errorCode } from '../../personnel/source.js';
import purchasing from '../../purchasing/Purchasing.module.css';
import { LoadError, NoMatch, TablePager, TableSkeleton } from '../../purchasing/table.js';
import { downloadFile } from '../../reports/xlsx.js';
import master from '../master/RaMaster.module.css';
import { LevelBadge } from '../RiskParts.js';
import { RA_REGISTER_PATH } from '../register/RaRegisterScreen.js';
import { RA_VERIFICATION_PATH } from '../verification/RaVerificationScreen.js';
import { verificationTone } from '../verification/view.js';
import classes from './RaDashboard.module.css';
import type { RaDashboardSource, RaReportsData } from './types.js';
import { complianceCsv, libraryCsv, matches, verificationCsv } from './view.js';

const PAGE_SIZE = 20;
/** Tarih süzgeci boş ya da `YYYY-MM-DD` (tarayıcı tarih alanı yazılırken geçersiz olabilir). */
const valid = (value: string) => value === '' || /^\d{4}-\d{2}-\d{2}$/.test(value);
export const RA_REPORT_KINDS = ['library', 'compliance', 'verifications'] as const;
export type RaReportKind = (typeof RA_REPORT_KINDS)[number];

const REVIEW_TONE = { ok: 'success', due: 'warning', overdue: 'danger' } as const;
const ACK_TONE = { acknowledged: 'success', pending: 'warning', not_required: 'neutral' } as const;

export interface RaReportsScreenProps {
  source: RaDashboardSource;
}

/**
 * Compliance ▸ Risk Assessment ▸ Monitoring & Reports (K7; ADR 0095 §2; R24; FD §2): RA
 * kütüphanesinin durumu, gemi register uyumu ve doğrulama geçmişi; süzgeç ve CSV. Gemide geminin
 * kendisi; ofiste filo ya da seçilen gemi. Sürüm ve doğrulama PDF'i kendi ekranlarındadır; satır
 * o ekranı açar. Tasarım yok; tablo dili Vessel Risk Register.
 */
export function RaReportsScreen({ source }: RaReportsScreenProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskDashboard.reports.${key}`, options);
  const shell = useShell();
  const [vesselId, setVesselId] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; view: RaReportsData }
  >({ kind: 'loading' });
  const load = useCallback(async () => {
    if (!valid(from) || !valid(to)) return;
    try {
      setState({
        kind: 'ready',
        view: await source.reports({
          vesselId,
          from: from || null,
          to: to || null,
        }),
      });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId, from, to]);
  useEffect(() => {
    void load();
  }, [load]);
  const download = async (fileName: string, csv: string) => {
    const bytes = new TextEncoder().encode(`﻿${csv}`);
    if (source.download) await source.download(fileName, bytes, 'text/csv');
    else downloadFile(fileName, bytes, 'text/csv;charset=utf-8');
  };
  const view = state.kind === 'ready' ? state.view : null;
  return (
    <div className={purchasing.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        icon={<IconReportAnalytics size={26} />}
      />
      <p className={master.note}>{source.scope === 'ship' ? k('shipNote') : k('officeNote')}</p>
      {state.kind === 'error' ? (
        <Panel>
          <LoadError title={k('error')} code={state.code} onRetry={() => void load()} />
        </Panel>
      ) : !view ? (
        <Panel flush aria-label={k('title')}>
          <TableSkeleton columns={8} label={k('title')} />
        </Panel>
      ) : (
        <RaReportsView
          view={view}
          scope={source.scope}
          range={{ from, to }}
          onRange={(next) => {
            setFrom(next.from);
            setTo(next.to);
          }}
          onVessel={setVesselId}
          onExport={download}
          onNavigate={(path) => shell?.navigate(path)}
        />
      )}
    </div>
  );
}

export interface RaReportsViewProps {
  view: RaReportsData;
  scope: 'ship' | 'web';
  range: { from: string; to: string };
  onRange: (range: { from: string; to: string }) => void;
  onVessel?: (vesselId: string | null) => void;
  onExport: (fileName: string, csv: string) => Promise<void>;
  onNavigate: (path: string) => void;
  initialKind?: RaReportKind;
}

/** Raporların görünümü: süzgeç, sayfa ve CSV burada; veri dışarıdan (test edilebilir). */
export function RaReportsView({
  view,
  scope,
  range,
  onRange,
  onVessel,
  onExport,
  onNavigate,
  initialKind,
}: RaReportsViewProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskDashboard.reports.${key}`, options);
  const c = (key: string) => k(`columns.${key}`);
  const kinds = RA_REPORT_KINDS.filter((kind) => kind !== 'library' || view.library !== null);
  const [kind, setKind] = useState<RaReportKind>(
    initialKind && kinds.includes(initialKind) ? initialKind : (kinds[0] ?? 'compliance'),
  );
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const fleet = scope === 'web' && view.vesselId === null;

  const library = useMemo(
    () =>
      (view.library ?? []).filter(
        (r) =>
          matches(query, r.code, r.title) &&
          (!status || r.status === status || r.review === status),
      ),
    [view.library, query, status],
  );
  const compliance = useMemo(
    () =>
      view.compliance.filter(
        (r) =>
          matches(query, r.code, r.title, r.vesselName) &&
          (!status ||
            r.acknowledgement === status ||
            r.review === status ||
            (status === 'late' && r.late)),
      ),
    [view.compliance, query, status],
  );
  const verifications = useMemo(
    () =>
      view.verifications.filter(
        (r) =>
          matches(query, r.number, r.operation, r.code, r.vesselName) &&
          (!status || r.status === status),
      ),
    [view.verifications, query, status],
  );
  const count =
    kind === 'library'
      ? library.length
      : kind === 'compliance'
        ? compliance.length
        : verifications.length;
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const current = Math.min(page, pages);
  const slice = <T,>(list: readonly T[]) =>
    list.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const statusOptions: { value: string; label: string }[] =
    kind === 'library'
      ? [
          ...['draft', 'submitted', 'under_review', 'returned', 'approved', 'published'].map(
            (s) => ({
              value: s,
              label: t(`riskLibrary.statuses.${s}`),
            }),
          ),
          { value: 'due', label: t('riskDashboard.review.due') },
          { value: 'overdue', label: t('riskDashboard.review.overdue') },
        ]
      : kind === 'compliance'
        ? [
            ...(['pending', 'acknowledged', 'not_required'] as const).map((s) => ({
              value: s,
              label: t(`riskRegister.acknowledgement.${s}`),
            })),
            { value: 'late', label: t('riskDashboard.attention.late') },
            { value: 'due', label: t('riskDashboard.review.due') },
            { value: 'overdue', label: t('riskDashboard.review.overdue') },
          ]
        : [...new Set(view.verifications.map((r) => r.status))].map((s) => ({
            value: s,
            label: t(`riskVerification.statuses.${s}`),
          }));
  const vesselTag = view.vesselId
    ? (view.vessels.find((v) => v.id === view.vesselId)?.name ?? 'vessel')
    : 'fleet';
  const exportCsv = () => {
    const tr = (key: string, options?: Record<string, unknown>) => String(t(key, options ?? {}));
    const csv =
      kind === 'library'
        ? libraryCsv(library, tr)
        : kind === 'compliance'
          ? complianceCsv(compliance, tr)
          : verificationCsv(verifications, tr);
    void onExport(`risk-${kind}-${vesselTag.replace(/[^A-Za-z0-9._-]+/g, '-')}.csv`, csv);
  };
  const empty = count === 0;
  const hasAny =
    kind === 'library'
      ? (view.library?.length ?? 0) > 0
      : kind === 'compliance'
        ? view.compliance.length > 0
        : view.verifications.length > 0;

  return (
    <Panel flush aria-label={k('title')}>
      <div className={classes.tabs}>
        <SegmentedControl
          aria-label={k('kind')}
          data={kinds.map((value) => ({ value, label: k(`kinds.${value}`) }))}
          value={kind}
          onChange={(value) => {
            setKind(value as RaReportKind);
            setStatus('');
            setPage(1);
          }}
        />
      </div>
      <div className={master.toolbar}>
        {scope === 'web' && onVessel && kind !== 'library' ? (
          <Select
            className={master.filter}
            label={t('riskDashboard.vessel')}
            data={[
              { value: '', label: t('riskDashboard.fleet') },
              ...view.vessels.map((v) => ({ value: v.id, label: v.name })),
            ]}
            value={view.vesselId ?? ''}
            allowDeselect={false}
            onChange={(value) => {
              onVessel(value ? value : null);
              setPage(1);
            }}
          />
        ) : null}
        <TextInput
          className={master.search}
          leftSection={<IconSearch size={16} aria-hidden />}
          placeholder={k('search')}
          aria-label={k('search')}
          value={query}
          onChange={(event) => {
            setQuery(event.currentTarget.value);
            setPage(1);
          }}
        />
        <Select
          className={master.filter}
          label={k('status')}
          data={[{ value: '', label: k('all') }, ...statusOptions]}
          value={status}
          allowDeselect={false}
          onChange={(value) => {
            setStatus(value ?? '');
            setPage(1);
          }}
        />
        {kind === 'verifications' ? (
          <>
            <TextInput
              className={master.filter}
              type="date"
              label={k('from')}
              value={range.from}
              onChange={(event) => onRange({ ...range, from: event.currentTarget.value })}
            />
            <TextInput
              className={master.filter}
              type="date"
              label={k('to')}
              value={range.to}
              onChange={(event) => onRange({ ...range, to: event.currentTarget.value })}
            />
          </>
        ) : null}
        <div className={master.actions}>
          <Button
            variant="default"
            leftSection={<IconDownload size={16} />}
            disabled={empty}
            onClick={exportCsv}
          >
            {k('export')}
          </Button>
        </div>
      </div>
      {!hasAny ? (
        <EmptyState title={k('empty.title')} description={k(`empty.${kind}`)} />
      ) : empty ? (
        <NoMatch
          title={k('empty.noMatch')}
          onClear={() => {
            setQuery('');
            setStatus('');
          }}
        />
      ) : (
        <>
          <section
            className={table.tableWrap}
            aria-label={k(`kinds.${kind}`)}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${table.table} ${purchasing.table}`}>
              {kind === 'library' ? (
                <>
                  <thead>
                    <tr>
                      <th scope="col">{c('code')}</th>
                      <th scope="col">{c('title')}</th>
                      <th scope="col">{c('status')}</th>
                      <th scope="col">{c('publishedRevision')}</th>
                      <th scope="col">{c('publishedAt')}</th>
                      <th scope="col">{c('matrix')}</th>
                      <th scope="col">{c('residual')}</th>
                      <th scope="col">{c('reviewDay')}</th>
                      <th scope="col">{c('assignedTo')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slice(library).map((r) => (
                      <tr key={r.assessmentId ?? r.code}>
                        <td>{r.code}</td>
                        <td className={master.wrapCell}>{r.title}</td>
                        <td>
                          <StatusBadge tone="neutral" withIcon={false}>
                            {t(`riskLibrary.statuses.${r.status}`)}
                          </StatusBadge>
                          {r.workingRevision && r.workingRevision !== r.publishedRevision ? (
                            <span className={master.sub}>
                              {k('working', { revision: r.workingRevision })}
                            </span>
                          ) : null}
                        </td>
                        <td>{r.publishedRevision ?? '—'}</td>
                        <td>{formatDay(r.publishedAt?.slice(0, 10))}</td>
                        <td>{r.matrixVersion === null ? k('legacy') : `v${r.matrixVersion}`}</td>
                        <td>
                          {r.residual ? (
                            <LevelBadge level={r.residual.level} score={r.residual.score} />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <span className={classes.itemMain}>
                            <span>{formatDay(r.reviewDay)}</span>
                            {r.review && r.review !== 'ok' ? (
                              <StatusBadge tone={REVIEW_TONE[r.review]} withIcon={false}>
                                {t(`riskDashboard.review.${r.review}`)}
                              </StatusBadge>
                            ) : null}
                          </span>
                        </td>
                        <td className={master.wrapCell}>{r.assignedTo.join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </>
              ) : kind === 'compliance' ? (
                <>
                  <thead>
                    <tr>
                      {fleet ? <th scope="col">{c('vessel')}</th> : null}
                      <th scope="col">{c('code')}</th>
                      <th scope="col">{c('title')}</th>
                      <th scope="col">{c('revision')}</th>
                      <th scope="col">{c('acknowledgement')}</th>
                      <th scope="col">{c('acknowledgedBy')}</th>
                      <th scope="col">{c('reviewDay')}</th>
                      <th scope="col">{c('assessments')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slice(compliance).map((r) => (
                      <tr key={`${r.vesselId}:${r.versionId}`}>
                        {fleet ? <td>{r.vesselName}</td> : null}
                        <td>
                          <button
                            type="button"
                            className={master.codeButton}
                            aria-label={k('openVersion', { code: r.code })}
                            onClick={() =>
                              onNavigate(
                                `${RA_REGISTER_PATH}?vessel=${r.vesselId}&version=${r.versionId}`,
                              )
                            }
                          >
                            {r.code}
                          </button>
                        </td>
                        <td className={master.wrapCell}>{r.title}</td>
                        <td>{r.revision}</td>
                        <td>
                          <StatusBadge
                            tone={r.late ? 'danger' : ACK_TONE[r.acknowledgement]}
                            withIcon={false}
                          >
                            {r.late
                              ? t('riskDashboard.attention.late')
                              : t(`riskRegister.acknowledgement.${r.acknowledgement}`)}
                          </StatusBadge>
                        </td>
                        <td>
                          {r.acknowledgedBy ? (
                            <span className={classes.itemMain}>
                              <span>
                                {r.acknowledgedBy}
                                {r.acknowledgedRole ? ` (${r.acknowledgedRole})` : ''}
                              </span>
                              <span className={classes.itemSub}>
                                {formatDateTime(r.acknowledgedAt, r.acknowledgedTzOffsetMin ?? 0)} (
                                {offsetLabel(r.acknowledgedTzOffsetMin ?? 0)})
                              </span>
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <span className={classes.itemMain}>
                            <span>{formatDay(r.reviewDay)}</span>
                            {r.review && r.review !== 'ok' ? (
                              <StatusBadge tone={REVIEW_TONE[r.review]} withIcon={false}>
                                {t(`riskDashboard.review.${r.review}`)}
                              </StatusBadge>
                            ) : null}
                          </span>
                        </td>
                        <td className={classes.num}>{r.assessments}</td>
                      </tr>
                    ))}
                  </tbody>
                </>
              ) : (
                <>
                  <thead>
                    <tr>
                      {fleet ? <th scope="col">{c('vessel')}</th> : null}
                      <th scope="col">{c('number')}</th>
                      <th scope="col">{c('operation')}</th>
                      <th scope="col">{c('code')}</th>
                      <th scope="col">{c('plannedStart')}</th>
                      <th scope="col">{c('answered')}</th>
                      <th scope="col">{c('residual')}</th>
                      <th scope="col">{c('status')}</th>
                      <th scope="col">{c('authorizedBy')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slice(verifications).map((r) => (
                      <tr key={r.id}>
                        {fleet ? <td>{r.vesselName}</td> : null}
                        <td>
                          <button
                            type="button"
                            className={master.codeButton}
                            aria-label={k('openVerification', { number: r.number })}
                            onClick={() =>
                              onNavigate(`${RA_VERIFICATION_PATH}?vessel=${r.vesselId}&id=${r.id}`)
                            }
                          >
                            {r.number}
                          </button>
                        </td>
                        <td className={master.wrapCell}>
                          {r.operation}
                          {r.lastStop ? (
                            <span className={master.sub}>
                              {k('stopped', { reason: r.lastStop.reason ?? '—' })}
                            </span>
                          ) : null}
                        </td>
                        <td>
                          {r.code} · {r.revision}
                        </td>
                        <td>
                          {r.plannedStart
                            ? `${formatDateTime(r.plannedStart, r.tzOffsetMin)} (${offsetLabel(r.tzOffsetMin)})`
                            : '—'}
                        </td>
                        <td className={classes.num}>
                          {r.answered}/{r.total}
                          {r.failed > 0 ? (
                            <span className={master.sub}>{k('failed', { count: r.failed })}</span>
                          ) : null}
                        </td>
                        <td>
                          {r.risk.residual ? (
                            <LevelBadge
                              level={r.risk.residual.level}
                              score={r.risk.residual.score}
                            />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <StatusBadge tone={verificationTone(r.status)} withIcon={false}>
                            {t(`riskVerification.statuses.${r.status}`)}
                          </StatusBadge>
                        </td>
                        <td>{r.authorizedByName ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </>
              )}
            </table>
          </section>
          <TablePager
            page={current}
            pages={pages}
            summary={k('showing', {
              from: (current - 1) * PAGE_SIZE + 1,
              to: Math.min(current * PAGE_SIZE, count),
              total: count,
            })}
            label={k('pagination')}
            onPage={setPage}
          />
        </>
      )}
    </Panel>
  );
}

/** Raporların yolu (menü). */
export const RA_REPORTS_PATH = '/compliance/risk-assessment/reports';
